import React, { useState, useEffect, useRef } from 'react';
import SEO from './SEO';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  PaperAirplaneIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  MagnifyingGlassIcon,
  StarIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon as SparklesIconSolid, LockClosedIcon } from '@heroicons/react/24/solid';
import jsPDF from 'jspdf';

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showInitialView, setShowInitialView] = useState(true);
  const [isApproved, setIsApproved] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const sampleQueries = [
    {
      icon: StarIcon,
      text: "What are the top 10 SharpeMetrix ranked stocks?",
      category: "Rankings"
    },
    {
      icon: ArrowTrendingUpIcon,
      text: "Show me technology stocks with the biggest rank improvements",
      category: "Momentum"
    },
    {
      icon: ChartBarIcon,
      text: "Which stocks have the highest momentum scores?",
      category: "Analysis"
    },
    {
      icon: MagnifyingGlassIcon,
      text: "Find finance sector stocks with strong value scores",
      category: "Sector"
    },
    {
      icon: ChartBarIcon,
      text: "What sectors are performing best this month?",
      category: "Sectors"
    },
    {
      icon: ArrowTrendingUpIcon,
      text: "Show me stocks with strong earnings scores",
      category: "Earnings"
    }
  ];

  useEffect(() => {
    // Generate session ID
    setSessionId(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  }, []);

  // Check user permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsApproved(response.data.is_approved === true);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setIsApproved(false);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // eslint-disable-next-line no-unused-vars
  const exportChatToPDF = async () => {
    if (messages.length === 0) return;

    // Load logo
    const loadLogo = () => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = '/logo_wordmark.png';
      });
    };

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = 0;

    // HEADER
    pdf.setFillColor(46, 91, 255);
    pdf.rect(0, 0, pageWidth, 28, 'F');
    
    const logoImg = await loadLogo();
    pdf.addImage(logoImg, 'PNG', 15, 8, 60, 12);
    
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    const title = 'AI Chat Export';
    const textWidth = pdf.getTextWidth(title);
    pdf.text(title, pageWidth - textWidth - 15, 18);
    
    y = 40;

    // Date
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const exportDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    pdf.text(`Exported: ${exportDate}`, 15, y);
    y += 10;

    // Messages
    for (const message of messages) {
      const isUser = message.type === 'user';
      
      // Check if we need a new page
      if (y > pageHeight - 40) {
        pdf.addPage();
        y = 25;
      }

      // Message header
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(isUser ? 46 : 60, isUser ? 91 : 120, isUser ? 255 : 180);
      pdf.text(isUser ? 'You' : 'SharpeMetrix AI', 15, y);
      y += 2;

      // Timestamp
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(150, 150, 150);
      const timeStr = message.timestamp.toLocaleTimeString();
      pdf.text(timeStr, 15, y);
      y += 5;

      // Message content
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(40, 40, 40);
      
      // Clean markdown for PDF (remove markdown syntax)
      let cleanContent = message.content
        .replace(/\*\*/g, '') // Remove bold
        .replace(/\*/g, '') // Remove italic
        .replace(/^#{1,6}\s/gm, '') // Remove headers
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // Remove links, keep text

      const lines = pdf.splitTextToSize(cleanContent, pageWidth - 30);
      
      for (const line of lines) {
        if (y > pageHeight - 30) {
          pdf.addPage();
          y = 25;
        }
        pdf.text(line, 20, y);
        y += 5;
      }
      
      y += 5; // Space between messages
    }

    // Footer disclaimer
    if (y > pageHeight - 40) {
      pdf.addPage();
      y = 25;
    }
    
    y = Math.max(y, pageHeight - 35);
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    pdf.setFont('helvetica', 'italic');
    const disclaimer = 'This report is generated by SharpeMetrix. Always seek advice from a Compliance professional when using SharpeMetrix.';
    const disclaimerLines = pdf.splitTextToSize(disclaimer, pageWidth - 30);
    pdf.text(disclaimerLines, 15, y);

    // Save
    const filename = `SharpeMetrix_Chat_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(filename);
  };



  const handleSampleQuery = async (query) => {
    setShowInitialView(false);
    setInputValue('');
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query,
      timestamp: new Date()
    };

    setMessages([userMessage]);
    setIsLoading(true);

    await processQuery(query, [userMessage]);
  };

  const processQuery = async (query, currentMessages) => {
    try {
      const token = localStorage.getItem('access_token');
      
      console.log('Sending query to backend...');
      
      // Send the query to our backend which will handle fetching stock data and OpenAI processing
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/chat/query`,
        {
          message: query,
          session_id: sessionId,
          conversation_history: messages.slice(-10) // Last 10 messages for context
        },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          },
        }
      );

      console.log('Backend response:', response.data);

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response.data.response || "I received an empty response. Please try rephrasing your question.",
        timestamp: new Date(),
        data_source: 'backend_analysis',
        stock_count: response.data.stock_count || 0
      };

      setMessages([...currentMessages, assistantMessage]);

    } catch (error) {
      console.error('Error processing query:', error);
      
      let errorContent = "I'm sorry, I encountered an error processing your request. ";
      
      if (error.response?.status === 401) {
        navigate('/login');
        return;
      } else {
        errorContent += "Please try again or rephrase your question.";
      }

      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: errorContent,
        error: true,
        timestamp: new Date()
      };

      setMessages([...currentMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    setShowInitialView(false);
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setIsLoading(true);

    await processQuery(userMessage.content, newMessages);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setShowInitialView(true);
    setSessionId(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  };



  // Helper function to check if message contains a table
  const hasTable = (content) => {
    // Check for markdown table syntax (| col1 | col2 |) or HTML table tags
    return (content.includes('|') && (content.includes('---') || content.match(/\|.*\|.*\|/))) || 
           content.toLowerCase().includes('<table');
  };

  // Export single message to PDF
  const exportMessageToPDF = async (message) => {
    const { generateDataTablePDF, formatPDFDate } = await import('../utils/pdfExport');
    
    // Parse the table from markdown content
    const lines = message.content.split('\n');
    let tableData = [];
    let headers = [];
    let inTable = false;
    
    for (const line of lines) {
      if (line.includes('|') && line.trim().startsWith('|')) {
        const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell && !cell.match(/^-+$/));
        if (cells.length > 0 && !line.includes('---')) {
          if (!inTable) {
            headers = cells;
            inTable = true;
          } else {
            tableData.push(cells);
          }
        }
      }
    }

    if (tableData.length === 0) {
      alert('No table data found in this message');
      return;
    }

    await generateDataTablePDF({
      title: 'AI Chat Response',
      subtitle: 'SharpeMetrix AI Analysis',
      columns: headers,
      data: tableData,
      filename: `SharpeMetrix_Chat_${new Date().toISOString().split('T')[0]}.pdf`,
      dateLabel: 'Generated',
      date: formatPDFDate(new Date())
    });
  };

  const renderMessage = (message) => {
    const isUser = message.type === 'user';
    const messageHasTable = !isUser && hasTable(message.content);
    
    return (
      <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`flex max-w-4xl ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start gap-4`}>
          {/* Avatar */}
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center shadow-lg ${
            isUser 
              ? 'bg-gradient-to-br from-blue-500 to-blue-600 ring-2 ring-blue-400/20' 
              : 'bg-gradient-to-br from-blue-600 to-blue-700 ring-2 ring-blue-400/20'
          }`}>
            {isUser ? (
              <span className="text-white text-sm font-bold">You</span>
            ) : (
              <SparklesIconSolid className="w-5 h-5 text-white" />
            )}
          </div>
          
          {/* Message Content */}
          <div className={`rounded-2xl px-6 py-4 shadow-lg backdrop-blur-sm ${
            isUser 
              ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white ring-1 ring-blue-400/20' 
              : message.error 
                ? 'bg-gradient-to-br from-red-900/40 to-red-800/40 text-red-200 ring-1 ring-red-500/30' 
                : 'bg-gradient-to-br from-slate-800/60 to-slate-900/60 text-slate-100 ring-1 ring-slate-700/50'
          }`}>
                          <div className="prose prose-sm max-w-none text-current leading-relaxed prose-invert">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom styling for markdown elements
                    h1: ({children}) => <h1 className="text-xl font-bold text-slate-100 mb-3 mt-4 first:mt-0">{children}</h1>,
                    h2: ({children}) => <h2 className="text-lg font-semibold text-slate-200 mb-2 mt-4 first:mt-0">{children}</h2>,
                    h3: ({children}) => <h3 className="text-base font-medium text-slate-300 mb-2 mt-3 first:mt-0">{children}</h3>,
                    p: ({children}) => <p className="mb-3 text-slate-200 leading-relaxed">{children}</p>,
                    strong: ({children}) => <strong className="font-semibold text-blue-300">{children}</strong>,
                    ul: ({children}) => <ul className="list-disc list-inside mb-3 space-y-1 text-slate-200">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal list-inside mb-3 space-y-1 text-slate-200">{children}</ol>,
                    li: ({children}) => <li className="text-slate-200">{children}</li>,
                    blockquote: ({children}) => (
                      <blockquote className="border-l-4 border-blue-500 pl-4 my-3 text-slate-300 italic bg-blue-500/10 py-2 rounded-r">
                        {children}
                      </blockquote>
                    ),
                    table: ({children}) => (
                      <div className="overflow-x-auto my-4 bg-slate-800/50 rounded-lg">
                        <table className="min-w-full border-collapse">
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({children}) => <thead className="bg-slate-700/70">{children}</thead>,
                    tbody: ({children}) => <tbody className="divide-y divide-slate-700/50">{children}</tbody>,
                    tr: ({children}) => <tr className="hover:bg-slate-700/30 transition-colors">{children}</tr>,
                    th: ({children}) => (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider border-b border-slate-600">
                        {children}
                      </th>
                    ),
                    td: ({children}) => (
                      <td className="px-4 py-3 text-sm text-slate-200 border-b border-slate-700/30">
                        {children}
                      </td>
                    ),
                    code: ({children, inline}) => 
                      inline ? (
                        <code className="bg-slate-700 text-blue-300 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
                      ) : (
                        <pre className="bg-slate-800 text-slate-200 p-3 rounded-lg overflow-x-auto my-3">
                          <code className="text-sm font-mono">{children}</code>
                        </pre>
                      )
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            
            {/* Show data source info */}
            {!isUser && message.data_source === 'backend_analysis' && message.stock_count > 0 && (
              <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-center gap-2 text-blue-400 text-sm">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>Analysis based on {message.stock_count} stocks from current month rankings</span>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between mt-3">
              <div className={`text-xs ${isUser ? 'text-blue-200' : 'text-slate-400'}`}>
                {message.timestamp.toLocaleTimeString()}
              </div>
              
              {/* Export PDF button - only show for assistant messages with tables */}
              {messageHasTable && (
                <button
                  onClick={() => exportMessageToPDF(message)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 rounded-lg transition-all duration-200"
                >
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                  <span>Export PDF</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Subscription banner for unapproved users
  const SubscriptionBanner = () => {
    if (isApproved) return null;
    
    return (
      <div className="mt-6 bg-gradient-to-r from-primary-100/20 to-accent-100/20 rounded-lg p-6 mx-4">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="mb-4 md:mb-0 md:mr-6">
            <h3 className="text-lg font-semibold text-text-100">
              <LockClosedIcon className="h-5 w-5 inline-block mr-2" />
              Unlock Full Access to AI-Powered Stock Analysis
            </h3>
            <p className="mt-1 text-text-200">
              Get unlimited access to our AI assistant for comprehensive stock analysis, rankings insights, and personalized research.
            </p>
          </div>
          <div>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center rounded-md bg-primary-100 px-4 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
            >
              Request Full Access
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 overflow-hidden">
      <SEO title="AI Research Assistant" description="Ask anything about models, tickers, or markets. Real answers, not generic responses." />
      {/* Beta Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 text-center text-sm font-medium flex-shrink-0">
        🚀 SharpeMetrix AI Assistant (Beta) - Your intelligent guide to stock rankings and market analysis
      </div>

      {showInitialView ? (
        /* Initial ChatGPT-style View */
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-1 min-h-0 overflow-hidden">
          <div className="max-w-4xl w-full flex flex-col justify-center min-h-0 max-h-full">
            {/* Logo and Title */}
            <div className="text-center mb-6">
              <div className="inline-flex p-3 bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-3xl ring-1 ring-blue-500/30 mb-4">
                <SparklesIconSolid className="w-10 h-10 text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold text-slate-100 mb-2">
                SharpeMetrix AI Assistant
              </h1>
              <p className="text-base text-slate-400 max-w-2xl mx-auto">
                Your intelligent guide to stock rankings, component scores, and market analysis
              </p>
            </div>

            {/* Large Search Bar */}
            <div className="bg-slate-800/60 rounded-2xl border border-slate-600/30 shadow-2xl backdrop-blur-sm mb-4">
              <div className="flex items-center gap-3 p-3">
                <div className="flex-1 relative min-w-0">
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isApproved ? "Ask me about top performers, sector analysis, component scores, or specific companies..." : "Full access required to use AI Assistant..."}
                    disabled={!isApproved}
                    className="w-full bg-transparent text-slate-100 placeholder-slate-400 resize-none focus:outline-none border-none text-sm leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
                    rows={1}
                    style={{ minHeight: '20px', maxHeight: '80px' }}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px';
                    }}
                  />
                </div>
                <button
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || isLoading || !isApproved}
                  className="bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed text-white p-2.5 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl ring-1 ring-blue-400/20 hover:ring-blue-400/40 disabled:ring-slate-500/20 flex-shrink-0"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            <SubscriptionBanner />

            {/* Sample Queries Grid */}
            {isApproved && (<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-1">
              {sampleQueries.map((query, index) => {
                const IconComponent = query.icon;
                return (
                  <button
                    key={index}
                    onClick={() => handleSampleQuery(query.text)}
                    disabled={isLoading}
                    className="group p-3 bg-gradient-to-br from-slate-800/60 to-slate-900/60 hover:from-slate-700/60 hover:to-slate-800/60 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-left transition-all duration-200 ring-1 ring-slate-700/50 hover:ring-slate-600/50 hover:shadow-lg"
                  >
                    <div className="flex items-start gap-2">
                      <div className="p-1.5 bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-lg group-hover:from-blue-600/30 group-hover:to-blue-800/30 transition-all duration-200 flex-shrink-0">
                        <IconComponent className="w-4 h-4 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-blue-400 mb-0.5 uppercase tracking-wider">
                          {query.category}
                        </div>
                        <div className="text-sm text-slate-200 group-hover:text-slate-100 transition-colors leading-tight">
                          {query.text}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>)}

            {/* Enterprise CTA Section */}
            {!isApproved && (
              <div className="mt-4">
                <div className="rounded-lg bg-gradient-to-r from-primary-100/10 to-accent-100/10 p-6">
                  <div className="text-center">
                    <h2 className="text-lg font-semibold text-text-100">
                      Want Access to AI-Powered Stock Analysis?
                    </h2>
                    <p className="mt-2 text-sm text-text-200">
                      Get unlimited access to our AI assistant for personalized stock analysis, rankings insights, 
                      custom research queries, and real-time market intelligence.
                    </p>
                    <div className="mt-4">
                      <button
                        onClick={() => navigate('/contact')}
                        className="inline-flex items-center rounded-md bg-primary-100 px-3.5 py-2.5 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
                      >
                        Contact Us for Enterprise Access
                        <span className="ml-2" aria-hidden="true">&rarr;</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center">
                <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-xl px-4 py-2 ring-1 ring-slate-700/50 shadow-lg backdrop-blur-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                    <span className="text-slate-300 text-xs">Analyzing...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Chat Messages View */
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="max-w-6xl mx-auto px-4 py-2">
            {/* Action Buttons */}
            <div className="flex justify-end mb-4 gap-2">
              <button
                onClick={clearChat}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-slate-100 hover:bg-slate-700/50 rounded-xl transition-all duration-200 ring-1 ring-slate-600/30 hover:ring-slate-500/50 backdrop-blur-sm"
              >
                <ArrowPathIcon className="w-4 h-4" />
                <span>New Chat</span>
              </button>
            </div>
            
            {messages.map(renderMessage)}
            
            {isLoading && (
              <div className="flex justify-start mb-6">
                <div className="flex items-start gap-4 max-w-4xl">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center ring-2 ring-blue-400/20 shadow-lg">
                    <SparklesIconSolid className="w-5 h-5 text-white animate-pulse" />
                  </div>
                  <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-2xl px-6 py-4 ring-1 ring-slate-700/50 shadow-lg backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                      <span className="text-slate-300 text-sm">Analyzing your query...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

            {/* Input Area - Only show in chat view */}
      {!showInitialView && (
        <div className="border-t border-slate-700/30 bg-slate-900/60 backdrop-blur-xl flex-shrink-0">
          <div className="max-w-6xl mx-auto px-4 py-2">
            <div className="bg-slate-800/60 rounded-2xl border border-slate-600/30 shadow-2xl backdrop-blur-sm">
              <div className="flex items-end gap-3 p-3">
                <div className="flex-1 relative min-w-0">
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isApproved ? "Ask me about SharpeMetrix rankings, component scores, or sector analysis..." : "Full access required to use AI Assistant..."}
                    disabled={!isApproved}
                    className="w-full bg-transparent text-slate-100 placeholder-slate-400 resize-none focus:outline-none border-none text-base leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    rows={1}
                    style={{ minHeight: '24px', maxHeight: '96px' }}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
                    }}
                  />
                </div>
                <button
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || isLoading || !isApproved}
                  className="bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed text-white p-2.5 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl ring-1 ring-blue-400/20 hover:ring-blue-400/40 disabled:ring-slate-500/20 flex-shrink-0"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="text-xs text-slate-500 mt-1 text-center">
              {isApproved ? "Press Enter to send • Shift+Enter for new line • Powered by SharpeMetrix AI" : "Full access required to use AI Assistant"}
            </div>
          </div>
        </div>
      )}
     </div>
   );
 } 